import React, { useState, useEffect } from 'react';
import ReactApexChart from 'react-apexcharts';
import { useParams } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import axiosInstance from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';

const RevenueChart = () => {
    const { companyId } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState('last_12_months');
    const [chartData, setChartData] = useState({
        series: [],
        options: {
            chart: {
                type: 'line', // Support mixed types
                height: 350,
                toolbar: {
                    show: false
                },
                background: 'transparent',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            },
            plotOptions: {
                bar: {
                    horizontal: false,
                    columnWidth: '60%',
                    endingShape: 'rounded',
                    borderRadius: 4
                },
            },
            dataLabels: {
                enabled: false
            },
            stroke: {
                width: [0, 0, 4], // Increased line width for better visibility
                curve: 'smooth'
            },
            markers: {
                size: [0, 0, 5], // Increased marker size for better targeting
                colors: ['transparent', 'transparent', '#8b5cf6'],
                strokeColors: ['transparent', 'transparent', '#ffffff'],
                strokeWidth: [0, 0, 2], // Increased stroke width
                hover: {
                    size: [0, 0, 8], // Much larger on hover for easier targeting
                    sizeOffset: 4 // Increased hover area
                }
            },
            xaxis: {
                categories: [],
                axisBorder: {
                    show: false
                },
                axisTicks: {
                    show: false
                },
                labels: {
                    style: {
                        colors: '#64748b',
                        fontSize: '12px',
                        fontWeight: 500
                    }
                },
                // Completely disable crosshairs on x-axis
                crosshairs: {
                    show: false,
                    position: 'front',
                    stroke: {
                        width: 0
                    }
                }
            },
            // Also make sure yaxis crosshairs are disabled:
            yaxis: {
                labels: {
                    formatter: function (val) {
                        return new Intl.NumberFormat('sv-SE', {
                            style: 'currency',
                            currency: 'SEK',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                            notation: 'compact',
                            compactDisplay: 'short'
                        }).format(val);
                    },
                    style: {
                        colors: '#64748b',
                        fontSize: '12px',
                        fontWeight: 500
                    }
                },
                // Disable crosshairs on y-axis
                crosshairs: {
                    show: false,
                    stroke: {
                        width: 0
                    }
                }
            },
            fill: {
                opacity: [0.8, 0.8, 1],
                type: ['gradient', 'gradient', 'solid'],
                gradient: {
                    shade: 'light',
                    type: 'vertical',
                    shadeIntensity: 0.2,
                    gradientToColors: ['#059669', '#b91c1c', undefined],
                    inverseColors: false,
                    opacityFrom: 0.8,
                    opacityTo: 1,
                    stops: [0, 100]
                }
            },
            tooltip: {
                enabled: true,
                shared: true, // Keep this for showing all data
                intersect: false,
                followCursor: false,
                hideDelay: 500,
                // Disable the crosshairs that highlight months
                marker: {
                    show: false
                },
                x: {
                    show: false // This removes the month highlighting
                },
                custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                    const categories = w.globals.categoryLabels || w.config.xaxis.categories;
                    const monthName = categories[dataPointIndex];

                    if (!series || dataPointIndex < 0) {
                        return '<div style="padding: 12px; background: white;">No data available</div>';
                    }

                    const revenue = series[0] ? series[0][dataPointIndex] || 0 : 0;
                    const expenses = series[1] ? series[1][dataPointIndex] || 0 : 0;
                    const netProfit = series[2] ? series[2][dataPointIndex] || 0 : 0;

                    const formatCurrency = (val) => {
                        return new Intl.NumberFormat('sv-SE', {
                            style: 'currency',
                            currency: 'SEK',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                        }).format(val || 0);
                    };

                    return `
            <div style="
                background: white; 
                padding: 16px; 
                border-radius: 8px; 
                box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                border: 1px solid #e2e8f0;
                font-family: system-ui, sans-serif;
                min-width: 180px;
            ">
                <div style="font-weight: 600; margin-bottom: 10px; color: #1e293b;">${monthName}</div>
                <div style="margin-bottom: 6px;">
                    <span style="color: #10b981;">●</span> 
                    <span style="color: #64748b; margin-left: 4px;">Intäkter: </span>
                    <strong style="color: #10b981;">${formatCurrency(revenue)}</strong>
                </div>
                <div style="margin-bottom: 6px;">
                    <span style="color: #ef4444;">●</span> 
                    <span style="color: #64748b; margin-left: 4px;">Kostnader: </span>
                    <strong style="color: #ef4444;">${formatCurrency(expenses)}</strong>
                </div>
                <div style="border-top: 1px solid #e2e8f0; padding-top: 8px; margin-top: 8px;">
                    <span style="color: #8b5cf6;">●</span> 
                    <span style="color: #64748b; margin-left: 4px;">Nettovinst: </span>
                    <strong style="color: ${netProfit >= 0 ? '#10b981' : '#ef4444'};">${formatCurrency(netProfit)}</strong>
                </div>
            </div>
        `;
                }
            },
            colors: ['#10b981', '#ef4444', '#8b5cf6'],
            legend: {
                position: 'top',
                horizontalAlign: 'left',
                fontSize: '14px',
                fontWeight: 500,
                labels: {
                    colors: '#374151'
                },
                markers: {
                    width: 8,
                    height: 8,
                    radius: 6
                }
            },
            grid: {
                borderColor: '#f1f5f9',
                strokeDashArray: 0,
                xaxis: {
                    lines: {
                        show: false
                    }
                },
                yaxis: {
                    lines: {
                        show: true
                    }
                }
            },
            // Disable states that might show hover effects
            states: {
                hover: {
                    filter: {
                        type: 'none'
                    }
                },
                active: {
                    filter: {
                        type: 'none'
                    }
                }
            },
            noData: {
                text: 'Laddar data...',
                align: 'center',
                verticalAlign: 'middle',
                style: {
                    color: '#64748b',
                    fontSize: '14px'
                }
            }
        }
    });

    const periodOptions = [
        { value: 'last_6_months', label: 'Senaste 6 månaderna' },
        { value: 'last_12_months', label: 'Senaste 12 månaderna' },
        { value: 'this_year', label: 'Detta år' }
    ];

    useEffect(() => {
        fetchRevenueData();
    }, [companyId, selectedPeriod]);

    const fetchRevenueData = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await axiosInstance.get(
                getApiUrl(`${companyId}/dashboard/revenue-chart`),
                {
                    params: { period: selectedPeriod },
                    withCredentials: true
                }
            );

            const { categories, series, monthlyDetails } = response.data;

            const formatCurrency = (val) => {
                return new Intl.NumberFormat('sv-SE', {
                    style: 'currency',
                    currency: 'SEK',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                }).format(val || 0);
            };

            setChartData(prevData => ({
                ...prevData,
                series: series,
                options: {
                    ...prevData.options,
                    xaxis: {
                        ...prevData.options.xaxis,
                        categories: categories
                    },
                    tooltip: {
                        ...prevData.options.tooltip,
                        custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                            const monthName = categories[dataPointIndex];
                            const details = monthlyDetails ? monthlyDetails[dataPointIndex] : null;

                            if (!series || dataPointIndex < 0) {
                                return '<div style="padding: 12px; background: white;">No data available</div>';
                            }

                            const revenue = series[0] ? series[0][dataPointIndex] || 0 : 0;
                            const expenses = series[1] ? series[1][dataPointIndex] || 0 : 0;
                            const netProfit = series[2] ? series[2][dataPointIndex] || 0 : 0;

                            // Build account details HTML
                            let revenueAccountsHtml = '';
                            let expenseAccountsHtml = '';

                            if (details && details.revenueAccounts && details.revenueAccounts.length > 0) {
                                const accountsToShow = details.revenueAccounts.slice(0, 5);
                                const remainingCount = details.revenueAccounts.length - 5;

                                revenueAccountsHtml = `
                                    <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e2e8f0;">
                                        <div style="font-size: 0.7rem; color: #64748b; margin-bottom: 4px;">Intäktskonton:</div>
                                        ${accountsToShow.map(acc => `
                                            <div style="display: flex; justify-content: space-between; font-size: 0.7rem; padding: 2px 0;">
                                                <span style="color: #374151;">${acc.accountNumber} ${acc.accountName.substring(0, 20)}${acc.accountName.length > 20 ? '...' : ''}</span>
                                                <span style="color: #10b981; font-weight: 500; margin-left: 8px;">${formatCurrency(acc.amount)}</span>
                                            </div>
                                        `).join('')}
                                        ${remainingCount > 0 ? `<div style="font-size: 0.65rem; color: #94a3b8; font-style: italic;">+${remainingCount} fler konton</div>` : ''}
                                    </div>
                                `;
                            }

                            if (details && details.expenseAccounts && details.expenseAccounts.length > 0) {
                                const accountsToShow = details.expenseAccounts.slice(0, 5);
                                const remainingCount = details.expenseAccounts.length - 5;

                                expenseAccountsHtml = `
                                    <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e2e8f0;">
                                        <div style="font-size: 0.7rem; color: #64748b; margin-bottom: 4px;">Kostnadskonton:</div>
                                        ${accountsToShow.map(acc => `
                                            <div style="display: flex; justify-content: space-between; font-size: 0.7rem; padding: 2px 0;">
                                                <span style="color: #374151;">${acc.accountNumber} ${acc.accountName.substring(0, 20)}${acc.accountName.length > 20 ? '...' : ''}</span>
                                                <span style="color: #ef4444; font-weight: 500; margin-left: 8px;">${formatCurrency(acc.amount)}</span>
                                            </div>
                                        `).join('')}
                                        ${remainingCount > 0 ? `<div style="font-size: 0.65rem; color: #94a3b8; font-style: italic;">+${remainingCount} fler konton</div>` : ''}
                                    </div>
                                `;
                            }

                            return `
                                <div style="
                                    background: white;
                                    padding: 16px;
                                    border-radius: 8px;
                                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                                    border: 1px solid #e2e8f0;
                                    font-family: system-ui, sans-serif;
                                    min-width: 220px;
                                    max-width: 320px;
                                ">
                                    <div style="font-weight: 600; margin-bottom: 10px; color: #1e293b;">${monthName}</div>
                                    <div style="margin-bottom: 6px;">
                                        <span style="color: #10b981;">●</span>
                                        <span style="color: #64748b; margin-left: 4px;">Intäkter: </span>
                                        <strong style="color: #10b981;">${formatCurrency(revenue)}</strong>
                                    </div>
                                    <div style="margin-bottom: 6px;">
                                        <span style="color: #ef4444;">●</span>
                                        <span style="color: #64748b; margin-left: 4px;">Kostnader: </span>
                                        <strong style="color: #ef4444;">${formatCurrency(expenses)}</strong>
                                    </div>
                                    <div style="border-top: 1px solid #e2e8f0; padding-top: 8px; margin-top: 8px;">
                                        <span style="color: #8b5cf6;">●</span>
                                        <span style="color: #64748b; margin-left: 4px;">Nettovinst: </span>
                                        <strong style="color: ${netProfit >= 0 ? '#10b981' : '#ef4444'};">${formatCurrency(netProfit)}</strong>
                                    </div>
                                    ${revenueAccountsHtml}
                                    ${expenseAccountsHtml}
                                </div>
                            `;
                        }
                    }
                }
            }));

        } catch (error) {
            console.error('Error fetching revenue data:', error);
            setError('Kunde inte ladda intäktsdata');
        } finally {
            setLoading(false);
        }
    };

    const handlePeriodChange = (period) => {
        setSelectedPeriod(period);
    };

    const getSelectedPeriodLabel = () => {
        const selected = periodOptions.find(option => option.value === selectedPeriod);
        return selected ? selected.label : 'Senaste 12 månaderna';
    };

    if (error) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '350px',
                background: 'transparent'
            }}>
                <div style={{
                    textAlign: 'center',
                    color: 'var(--secondary)'
                }}>
                    <p>{error}</p>
                    <button
                        onClick={fetchRevenueData}
                        className="dashboard-action-btn"
                        style={{ padding: '0.5rem 1rem' }}
                    >
                        Försök igen
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ background: 'transparent' }}>
            {/* Period Selection Dropdown */}
            <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginBottom: '1rem'
            }}>
                <Dropdown>
                    <Dropdown.Toggle
                        as="button"
                        className="dashboard-dropdown dropdown-toggle"
                    >
                        {getSelectedPeriodLabel()}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {periodOptions.map(option => (
                            <Dropdown.Item
                                key={option.value}
                                onClick={() => handlePeriodChange(option.value)}
                                active={selectedPeriod === option.value}
                            >
                                {option.label}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
            </div>

            {/* Loading state */}
            {loading && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '350px'
                }}>
                    <div>Laddar...</div>
                </div>
            )}

            {/* Chart */}
            {!loading && (
                <ReactApexChart
                    options={chartData.options}
                    series={chartData.series}
                    type="line"
                    height={350}
                />
            )}
        </div>
    );
};

export default RevenueChart;



// import React, { useState, useEffect } from 'react';
// import ReactApexChart from 'react-apexcharts';
// import { useParams } from 'react-router-dom';
// import { Dropdown } from 'react-bootstrap';
// import axiosInstance from '../../../../services/AxiosInstance';
// import { getApiUrl } from '../../../../utils/ApiUtils';

// const RevenueChart = () => {
//     const { companyId } = useParams();
//     const [loading, setLoading] = useState(true);
//     const [error, setError] = useState(null);
//     const [selectedPeriod, setSelectedPeriod] = useState('last_12_months');
//     const [chartData, setChartData] = useState({
//         series: [],
//         options: {
//             chart: {
//                 type: 'line', // Support mixed types
//                 height: 350,
//                 toolbar: {
//                     show: false
//                 },
//                 background: 'transparent',
//                 fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
//             },
//             plotOptions: {
//                 bar: {
//                     horizontal: false,
//                     columnWidth: '60%',
//                     endingShape: 'rounded',
//                     borderRadius: 4
//                 },
//             },
//             dataLabels: {
//                 enabled: false
//             },
//             stroke: {
//                 width: [0, 0, 3], // No stroke for bars, 3px for line
//                 curve: 'smooth'
//             },
//             markers: {
//                 size: [0, 0, 6], // No markers for bars, 6px markers for line
//                 colors: ['transparent', 'transparent', '#8b5cf6'],
//                 strokeColors: ['transparent', 'transparent', '#ffffff'],
//                 strokeWidth: [0, 0, 2],
//                 hover: {
//                     size: [0, 0, 8], // Larger on hover for the line
//                     sizeOffset: 2
//                 }
//             },
//             xaxis: {
//                 categories: [],
//                 axisBorder: {
//                     show: false
//                 },
//                 axisTicks: {
//                     show: false
//                 },
//                 labels: {
//                     style: {
//                         colors: '#64748b',
//                         fontSize: '12px',
//                         fontWeight: 500
//                     }
//                 }
//             },
//             yaxis: {
//                 // Single Y-axis for all series
//                 labels: {
//                     formatter: function (val) {
//                         return new Intl.NumberFormat('sv-SE', {
//                             style: 'currency',
//                             currency: 'SEK',
//                             minimumFractionDigits: 0,
//                             maximumFractionDigits: 0,
//                             notation: 'compact',
//                             compactDisplay: 'short'
//                         }).format(val);
//                     },
//                     style: {
//                         colors: '#64748b',
//                         fontSize: '12px',
//                         fontWeight: 500
//                     }
//                 },
//                 // Add a horizontal line at zero for reference
//                 crosshairs: {
//                     show: true,
//                     position: 'front',
//                     stroke: {
//                         color: '#374151',
//                         width: 1,
//                         dashArray: 3
//                     }
//                 }
//             },
//             fill: {
//                 opacity: [0.8, 0.8, 1], // Different opacity for line vs bars
//                 type: ['gradient', 'gradient', 'solid'],
//                 gradient: {
//                     shade: 'light',
//                     type: 'vertical',
//                     shadeIntensity: 0.2,
//                     gradientToColors: ['#059669', '#b91c1c', undefined],
//                     inverseColors: false,
//                     opacityFrom: 0.8,
//                     opacityTo: 1,
//                     stops: [0, 100]
//                 }
//             },
//             tooltip: {
//                 shared: true,
//                 intersect: false,
//                 custom: function ({ series, seriesIndex, dataPointIndex, w }) {
//                     const categories = w.globals.categoryLabels;
//                     const monthName = categories[dataPointIndex];

//                     const revenue = series[0][dataPointIndex];
//                     const expenses = series[1][dataPointIndex];
//                     const netProfit = series[2][dataPointIndex];

//                     const formatCurrency = (val) => {
//                         return new Intl.NumberFormat('sv-SE', {
//                             style: 'currency',
//                             currency: 'SEK',
//                             minimumFractionDigits: 0,
//                             maximumFractionDigits: 0
//                         }).format(val);
//                     };

//                     return `
//                     <div style="
//                         background: white; 
//                         padding: 12px; 
//                         border-radius: 8px; 
//                         box-shadow: 0 4px 12px rgba(0,0,0,0.1);
//                         border: 1px solid #e2e8f0;
//                         font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
//                     ">
//                         <div style="font-weight: 600; margin-bottom: 8px; color: #1e293b;">${monthName}</div>
//                         <div style="display: flex; align-items: center; margin-bottom: 4px;">
//                             <div style="
//                                 width: 12px; 
//                                 height: 12px; 
//                                 background: #10b981; 
//                                 border-radius: 50%; 
//                                 margin-right: 8px;
//                             "></div>
//                             <span style="color: #64748b; font-size: 13px;">Intäkter:</span>
//                             <span style="font-weight: 600; margin-left: 8px; color: #10b981;">${formatCurrency(revenue)}</span>
//                         </div>
//                         <div style="display: flex; align-items: center; margin-bottom: 4px;">
//                             <div style="
//                                 width: 12px; 
//                                 height: 12px; 
//                                 background: #ef4444; 
//                                 border-radius: 50%; 
//                                 margin-right: 8px;
//                             "></div>
//                             <span style="color: #64748b; font-size: 13px;">Kostnader:</span>
//                             <span style="font-weight: 600; margin-left: 8px; color: #ef4444;">${formatCurrency(expenses)}</span>
//                         </div>
//                         <div style="
//                             border-top: 1px solid #e2e8f0; 
//                             margin-top: 8px; 
//                             padding-top: 8px;
//                             display: flex; 
//                             align-items: center;
//                         ">
//                             <div style="
//                                 width: 12px; 
//                                 height: 12px; 
//                                 background: #8b5cf6; 
//                                 border-radius: 50%; 
//                                 margin-right: 8px;
//                             "></div>
//                             <span style="color: #64748b; font-size: 13px;">Nettovinst:</span>
//                             <span style="
//                                 font-weight: 600; 
//                                 margin-left: 8px; 
//                                 color: ${netProfit >= 0 ? '#10b981' : '#ef4444'};
//                             ">${formatCurrency(netProfit)}</span>
//                         </div>
//                     </div>
//                 `;
//                 }
//             },
//             colors: ['#10b981', '#ef4444', '#8b5cf6'],
//             legend: {
//                 position: 'top',
//                 horizontalAlign: 'left',
//                 fontSize: '14px',
//                 fontWeight: 500,
//                 labels: {
//                     colors: '#374151'
//                 },
//                 markers: {
//                     width: 12,
//                     height: 12,
//                     radius: 6
//                 }
//             },
//             grid: {
//                 borderColor: '#f1f5f9',
//                 strokeDashArray: 0,
//                 xaxis: {
//                     lines: {
//                         show: false
//                     }
//                 },
//                 yaxis: {
//                     lines: {
//                         show: true
//                     }
//                 },
//                 // Add a line at zero
//                 row: {
//                     colors: undefined,
//                     opacity: 0.5
//                 }
//             },
//             annotations: {
//                 yaxis: [
//                     {
//                         y: 0,
//                         borderColor: '#374151',
//                         borderWidth: 1,
//                         strokeDashArray: 3,
//                         opacity: 0.8
//                     }
//                 ]
//             },
//             noData: {
//                 text: 'Laddar data...',
//                 align: 'center',
//                 verticalAlign: 'middle',
//                 style: {
//                     color: '#64748b',
//                     fontSize: '14px'
//                 }
//             }
//         }
//     });

//     const periodOptions = [
//         { value: 'last_6_months', label: 'Senaste 6 månaderna' },
//         { value: 'last_12_months', label: 'Senaste 12 månaderna' },
//         { value: 'this_year', label: 'Detta år' }
//     ];

//     useEffect(() => {
//         fetchRevenueData();
//     }, [companyId, selectedPeriod]);

//     const fetchRevenueData = async () => {
//         try {
//             setLoading(true);
//             setError(null);

//             const response = await axiosInstance.get(
//                 getApiUrl(`${companyId}/dashboard/revenue-chart`),
//                 {
//                     params: { period: selectedPeriod },
//                     withCredentials: true
//                 }
//             );

//             const { categories, series } = response.data;

//             setChartData(prevData => ({
//                 ...prevData,
//                 series: series,
//                 options: {
//                     ...prevData.options,
//                     xaxis: {
//                         ...prevData.options.xaxis,
//                         categories: categories
//                     }
//                 }
//             }));

//         } catch (error) {
//             console.error('Error fetching revenue data:', error);
//             setError('Kunde inte ladda intäktsdata');
//         } finally {
//             setLoading(false);
//         }
//     };

//     const handlePeriodChange = (period) => {
//         setSelectedPeriod(period);
//     };

//     const getSelectedPeriodLabel = () => {
//         const selected = periodOptions.find(option => option.value === selectedPeriod);
//         return selected ? selected.label : 'Senaste 12 månaderna';
//     };

//     if (error) {
//         return (
//             <div style={{
//                 display: 'flex',
//                 justifyContent: 'center',
//                 alignItems: 'center',
//                 height: '350px',
//                 background: 'transparent'
//             }}>
//                 <div style={{
//                     textAlign: 'center',
//                     color: '#64748b'
//                 }}>
//                     <p>{error}</p>
//                     <button
//                         onClick={fetchRevenueData}
//                         style={{
//                             padding: '0.5rem 1rem',
//                             border: '1px solid #e2e8f0',
//                             borderRadius: '8px',
//                             background: 'white',
//                             color: '#374151',
//                             cursor: 'pointer'
//                         }}
//                     >
//                         Försök igen
//                     </button>
//                 </div>
//             </div>
//         );
//     }

//     return (
//         <div style={{ background: 'transparent' }}>
//             {/* Period Selection Dropdown */}
//             <div style={{
//                 display: 'flex',
//                 justifyContent: 'flex-end',
//                 marginBottom: '1rem'
//             }}>
//                 <Dropdown>
//                     <Dropdown.Toggle
//                         variant="outline-secondary"
//                         size="sm"
//                         style={{
//                             borderRadius: '8px',
//                             border: '1px solid #e2e8f0',
//                             color: '#64748b',
//                             fontSize: '0.875rem'
//                         }}
//                     >
//                         {getSelectedPeriodLabel()}
//                     </Dropdown.Toggle>
//                     <Dropdown.Menu>
//                         {periodOptions.map(option => (
//                             <Dropdown.Item
//                                 key={option.value}
//                                 onClick={() => handlePeriodChange(option.value)}
//                                 active={selectedPeriod === option.value}
//                             >
//                                 {option.label}
//                             </Dropdown.Item>
//                         ))}
//                     </Dropdown.Menu>
//                 </Dropdown>
//             </div>

//             {/* Chart */}
//             <ReactApexChart
//                 options={chartData.options}
//                 series={chartData.series}
//                 type="line"
//                 height={350}
//             />
//         </div>
//     );
// };

// export default RevenueChart;

// // import React, { useState, useEffect } from 'react';
// // import ReactApexChart from 'react-apexcharts';
// // import { useParams } from 'react-router-dom';
// // import axiosInstance from '../../../../services/AxiosInstance';
// // import { getApiUrl } from '../../../../utils/ApiUtils';

// // const RevenueChart = () => {
// //     const { companyId } = useParams();
// //     const [chartData, setChartData] = useState({
// //         series: [{
// //             name: 'Intäkter',
// //             data: [44000, 55000, 57000, 56000, 61000, 58000, 63000, 60000, 66000, 65000, 68000, 72000]
// //         }, {
// //             name: 'Kostnader',
// //             data: [35000, 41000, 36000, 26000, 45000, 48000, 52000, 53000, 41000, 42000, 45000, 48000]
// //         }],
// //         options: {
// //             chart: {
// //                 type: 'bar',
// //                 height: 350,
// //                 toolbar: {
// //                     show: false
// //                 },
// //                 background: 'transparent',
// //                 fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
// //             },
// //             plotOptions: {
// //                 bar: {
// //                     horizontal: false,
// //                     columnWidth: '60%',
// //                     endingShape: 'rounded',
// //                     borderRadius: 4
// //                 },
// //             },
// //             dataLabels: {
// //                 enabled: false
// //             },
// //             stroke: {
// //                 show: true,
// //                 width: 0,
// //                 colors: ['transparent']
// //             },
// //             xaxis: {
// //                 categories: ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'],
// //                 axisBorder: {
// //                     show: false
// //                 },
// //                 axisTicks: {
// //                     show: false
// //                 },
// //                 labels: {
// //                     style: {
// //                         colors: '#64748b',
// //                         fontSize: '12px',
// //                         fontWeight: 500
// //                     }
// //                 }
// //             },
// //             yaxis: {
// //                 labels: {
// //                     formatter: function (val) {
// //                         return new Intl.NumberFormat('sv-SE', {
// //                             style: 'currency',
// //                             currency: 'SEK',
// //                             minimumFractionDigits: 0,
// //                             maximumFractionDigits: 0,
// //                             notation: 'compact',
// //                             compactDisplay: 'short'
// //                         }).format(val);
// //                     },
// //                     style: {
// //                         colors: '#64748b',
// //                         fontSize: '12px',
// //                         fontWeight: 500
// //                     }
// //                 }
// //             },
// //             fill: {
// //                 opacity: 1,
// //                 type: 'gradient',
// //                 gradient: {
// //                     shade: 'light',
// //                     type: 'vertical',
// //                     shadeIntensity: 0.2,
// //                     gradientToColors: ['#059669', '#b91c1c'],
// //                     inverseColors: false,
// //                     opacityFrom: 0.8,
// //                     opacityTo: 1,
// //                     stops: [0, 100]
// //                 }
// //             },
// //             tooltip: {
// //                 y: {
// //                     formatter: function (val) {
// //                         return new Intl.NumberFormat('sv-SE', {
// //                             style: 'currency',
// //                             currency: 'SEK',
// //                             minimumFractionDigits: 0,
// //                             maximumFractionDigits: 0
// //                         }).format(val);
// //                     }
// //                 },
// //                 style: {
// //                     fontSize: '12px',
// //                     fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
// //                 }
// //             },
// //             colors: ['#10b981', '#ef4444'],
// //             legend: {
// //                 position: 'top',
// //                 horizontalAlign: 'left',
// //                 fontSize: '14px',
// //                 fontWeight: 500,
// //                 labels: {
// //                     colors: '#374151'
// //                 },
// //                 markers: {
// //                     width: 12,
// //                     height: 12,
// //                     radius: 6
// //                 }
// //             },
// //             grid: {
// //                 borderColor: '#f1f5f9',
// //                 strokeDashArray: 0,
// //                 xaxis: {
// //                     lines: {
// //                         show: false
// //                     }
// //                 },
// //                 yaxis: {
// //                     lines: {
// //                         show: true
// //                     }
// //                 }
// //             }
// //         }
// //     });

// //     useEffect(() => {
// //         fetchRevenueData();
// //     }, [companyId]);

// //     const fetchRevenueData = async () => {
// //         try {
// //             // Replace with actual API call
// //             // const response = await axiosInstance.get(getApiUrl(`${companyId}/dashboard/revenue-chart`));
// //             // setChartData(response.data);
// //         } catch (error) {
// //             console.error('Error fetching revenue data:', error);
// //         }
// //     };

// //     return (
// //         <div style={{ background: 'transparent' }}>
// //             <ReactApexChart
// //                 options={chartData.options}
// //                 series={chartData.series}
// //                 type="bar"
// //                 height={350}
// //             />
// //         </div>
// //     );
// // };

// // export default RevenueChart;